// ============================================================
// authConfig.js
// MSAL configuration for the A2Z CareHub admin portal.
//
// Authenticates against the A2Z IT M365 tenant (a2zit.ai), NOT
// the External ID tenant the user-facing app uses. Only A2Z IT
// staff sign in here, and a UPN allowlist (enforced separately
// in ProtectedRoute) gates which staff are permitted.
//
// Build-time configuration is sourced from environment variables
// (REACT_APP_*) which the pipeline injects from Key Vault. See
// azure-pipelines.yml Stage 8 for the wiring.
// ============================================================

const CLIENT_ID = process.env.REACT_APP_ADMIN_APP_CLIENT_ID || '';
const TENANT_ID = process.env.REACT_APP_ADMIN_TENANT_ID || '';
const REDIRECT_URI =
  process.env.REACT_APP_ADMIN_REDIRECT_URI || window.location.origin;

export const msalConfig = {
  auth: {
    clientId: CLIENT_ID,
    authority: `https://login.microsoftonline.com/${TENANT_ID}`,
    redirectUri: REDIRECT_URI,
    postLogoutRedirectUri: REDIRECT_URI,
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        if (process.env.NODE_ENV === 'development') {
          console.log(`[MSAL Admin] ${message}`);
        }
      },
    },
  },
};

export const loginRequest = {
  scopes: ['openid', 'profile', 'email', 'User.Read'],
};

// API scope for the CareHub admin Function App (mgmt/* endpoints).
// Separated from loginRequest so authentication (who are you) and
// authorisation (what API can you call) stay decoupled — sign-in
// only fetches OIDC scopes; the API token is acquired lazily on
// the first mgmt/* call via acquireTokenSilent. MSAL caches it
// per-scope and refreshes transparently.
//
// Value matches infra/bicep/modules/functions-flex.bicep JWT_ADMIN_AUDIENCE
// + the exposed scope name `access_as_admin` on the api-carehub-admin
// app registration. Tracking record in
// docs/operations/jwt-cutover-runbook.md §A.4.
export const apiRequest = {
  scopes: ['api://api.a2zit.ai/carehub-admin/access_as_admin'],
};

// ── UPN allowlist ─────────────────────────────────────────────
// Comma-separated list injected at build time from Key Vault
// (adminUpnAllowlist). Trimmed and lowercased for comparison.
const RAW_ALLOWLIST = process.env.REACT_APP_ADMIN_UPN_ALLOWLIST || '';

export const adminUpnAllowlist = RAW_ALLOWLIST
  .split(',')
  .map((upn) => upn.trim().toLowerCase())
  .filter(Boolean);

export function isUpnAllowed(upn) {
  if (!upn) return false;
  return adminUpnAllowlist.includes(upn.toLowerCase());
}
